import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiRefreshCw,
  FiBriefcase,
  FiSearch,
  FiShield,
  FiHash,
  FiInfo,
  FiX,
  FiCheck,
  FiSlash,
  FiFileText
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { assetsApi, legalReviewFirmsApi, legalReviewsApi } from '../../api/smartTokenizerApi';
import { formatEnumLabel, formatSectorLabel, mapAssetToFrontend } from '../../api/smartTokenizerMappers';
import { getStoredSession } from '../../api/authStorage';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const nestedAsset = (review) => review?.asset ?? review?.Asset ?? null;

const assetTitle = (a) => pick(a, 'title', 'Title') || 'Untitled asset';
const assetSectorRaw = (a) => pick(a, 'sector', 'Sector');
const assetNumber = (a) => pick(a, 'assetNumber', 'AssetNumber');
const assetCountry = (a) => pick(a, 'country', 'Country');
const assetStatusRaw = (a) => pick(a, 'status', 'Status');
const tokenizerOrg = (a) => {
  const t = a?.authorizedTokenizer ?? a?.AuthorizedTokenizer;
  return pick(t, 'organizationName', 'OrganizationName');
};
const reviewIdStr = (r) => String(pick(r, 'id', 'Id') ?? '');
const reviewAssetId = (r) => pick(r, 'assetId', 'AssetId');
const reviewAssignedReviewerId = (r) => pick(r, 'assignedReviewerId', 'AssignedReviewerId');

const sameGuidString = (a, b) =>
  a != null &&
  b != null &&
  String(a).replace(/-/g, '').toLowerCase() === String(b).replace(/-/g, '').toLowerCase();

function getReviewStatusMeta(review) {
  const raw = review?.status ?? review?.Status;
  const n = typeof raw === 'number' ? raw : null;
  const s = String(raw ?? '');
  if (n === 1 || /pendingassignment/i.test(s)) {
    return { tone: 'pending', label: 'Awaiting assignment', tabKey: 'pending' };
  }
  if (n === 2 || /inreview/i.test(s)) {
    return { tone: 'inreview', label: 'In review', tabKey: 'inreview' };
  }
  if (n === 3 || /^approved$/i.test(s)) {
    return { tone: 'approved', label: 'Approved', tabKey: 'approved' };
  }
  if (n === 4 || /reject/i.test(s)) {
    return { tone: 'rejected', label: 'Rejected', tabKey: 'rejected' };
  }
  return { tone: 'default', label: formatEnumLabel(raw) || '—', tabKey: 'other' };
}

function getReviewStatusTabKey(review) {
  return getReviewStatusMeta(review).tabKey;
}

const LEGAL_STATUS_TAB_LABELS = {
  pending: 'Awaiting assignment',
  inreview: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  other: 'Other'
};

const ASSET_STATUS_NAMES = [
  '',
  'Draft',
  'SubmittedForLegalReview',
  'UnderLegalReview',
  'ApprovedForTokenization',
  'Rejected',
  'MetadataPinnedToIpfs',
  'MintRequested',
  'MintingInProgress',
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized'
];

function assetStatusCanonical(status) {
  if (status === null || status === undefined) return 'Unknown';
  if (typeof status === 'number') {
    if (status >= 1 && status < ASSET_STATUS_NAMES.length) {
      return ASSET_STATUS_NAMES[status];
    }
    return `Unknown(${status})`;
  }
  const str = String(status).trim();
  if (!str) return 'Unknown';
  const exact = ASSET_STATUS_NAMES.find((n) => n === str);
  if (exact) return exact;
  const fold = str.replace(/\s+/g, '');
  const ci = ASSET_STATUS_NAMES.find((n) => n.toLowerCase() === fold.toLowerCase());
  if (ci) return ci;
  return str;
}

function assetStatusSortIndex(canonical) {
  const i = ASSET_STATUS_NAMES.indexOf(canonical);
  return i === -1 ? 999 : i;
}

const Page = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  color: #fff;
`;

const Hero = styled.div`
  margin-bottom: 1.5rem;

  h1 {
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    color: #a0a0a0;
    line-height: 1.6;
    max-width: 800px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.2rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$variant === 'secondary' ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #4facfe, #00f2fe)')};
  color: #fff;
  border: ${(p) => (p.$variant === 'secondary' ? '1px solid rgba(255,255,255,0.2)' : 'none')};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Notice = styled.div`
  background: rgba(79, 172, 254, 0.12);
  border: 1px solid rgba(79, 172, 254, 0.35);
  color: #cfe9ff;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const FilterBlock = styled.div`
  margin-bottom: 1rem;
`;

const FilterLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 0.4rem;
`;

const FilterTabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const FilterTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$active ? 'rgba(79, 172, 254, 0.18)' : 'rgba(0, 0, 0, 0.22)')};
  color: ${(p) => (p.$active ? '#e0f2fe' : '#94a3b8')};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  max-width: 100%;

  &:hover {
    border-color: rgba(79, 172, 254, 0.35);
    color: ${(p) => (p.$active ? '#e0f2fe' : '#cbd5e1')};
  }
`;

const FilterCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  background: ${(p) => (p.$active ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.08)')};
  color: ${(p) => (p.$active ? '#fff' : '#a0a0a0')};
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
  color: #a0a0a0;

  input {
    flex: 1;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 0.9rem;
    outline: none;
    min-width: 0;

    &::placeholder {
      color: #6b7280;
    }
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  th {
    color: #94a3b8;
    font-weight: 700;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  tbody tr:hover {
    background: rgba(79, 172, 254, 0.06);
  }
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  background: rgba(79, 172, 254, 0.15);
  color: #9bd6ff;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const Empty = styled.div`
  text-align: center;
  padding: 2rem 1.25rem;
  color: #94a3b8;
  border-radius: 12px;
  border: 1px dashed rgba(79, 172, 254, 0.25);
  background: rgba(79, 172, 254, 0.04);

  strong {
    display: block;
    color: #e2e8f0;
    margin-bottom: 0.35rem;
  }
`;

const Mono = styled.span`
  font-family: ui-monospace, monospace;
  font-size: 0.75rem;
  color: #cbd5e1;
`;

const ActionsCell = styled.td`
  white-space: nowrap;
  width: 1%;
`;

const IconOnlyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.45);
  background: rgba(79, 172, 254, 0.1);
  color: #9bd6ff;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(79, 172, 254, 0.22);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const RowActionsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
  justify-content: flex-end;
  max-width: 300px;
`;

const RowActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.5rem;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border: 1px solid
    ${(p) =>
      p.$tone === 'ok'
        ? 'rgba(74, 222, 128, 0.45)'
        : p.$tone === 'bad'
          ? 'rgba(248, 113, 113, 0.5)'
          : 'rgba(148, 163, 184, 0.45)'};
  background: ${(p) =>
    p.$tone === 'ok'
      ? 'rgba(74, 222, 128, 0.12)'
      : p.$tone === 'bad'
        ? 'rgba(248, 113, 113, 0.12)'
        : 'rgba(148, 163, 184, 0.12)'};
  color: ${(p) =>
    p.$tone === 'ok' ? '#bbf7d0' : p.$tone === 'bad' ? '#fecaca' : '#e2e8f0'};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ModalTextArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.35);
  color: #f1f5f9;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: rgba(79, 172, 254, 0.5);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(145deg, #151528 0%, #1a1a38 100%);
  border: 1px solid rgba(79, 172, 254, 0.25);
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 0.5rem 1rem;
  margin: 0 0 1.25rem 0;
  font-size: 0.9rem;

  dt {
    color: #a0a0a0;
    margin: 0;
  }

  dd {
    margin: 0;
    color: #f0f0f0;
    word-break: break-word;
  }
`;

const JsonBlock = styled.pre`
  margin: 0;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  font-size: 0.75rem;
  line-height: 1.45;
  overflow-x: auto;
  color: #c8e6c9;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem 0.4rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  th {
    color: #a0a0a0;
    font-weight: 600;
  }
`;

const LegalFirmAssets = () => {
  const {
    canAccessLegalReviewApi,
    canAssignOrDecideLegalReview,
    isAdmin,
    isAuditor,
    isLegalReviewer,
    currentIssuer
  } = useTokenIssuer();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [legalStatusTab, setLegalStatusTab] = useState('all');
  const [assetStatusTab, setAssetStatusTab] = useState('all');
  const [firmLabel, setFirmLabel] = useState('');
  const [submittingReviewId, setSubmittingReviewId] = useState(null);
  const [decisionModal, setDecisionModal] = useState({
    open: false,
    mode: null,
    reviewId: null,
    notes: ''
  });
  const [detailModal, setDetailModal] = useState({
    open: false,
    loading: false,
    error: null,
    mapped: null,
    raw: null
  });

  const myUserId = currentIssuer?.userId ?? currentIssuer?.id ?? null;

  const canDecideAssignedReview = (r) => {
    if (!canAssignOrDecideLegalReview) return false;
    if (getReviewStatusTabKey(r) !== 'inreview') return false;
    if (isAdmin) return true;
    if (!isLegalReviewer || !myUserId) return false;
    return sameGuidString(reviewAssignedReviewerId(r), myUserId);
  };

  const session = getStoredSession();
  const signedIn = Boolean(session?.accessToken);

  const load = useCallback(async () => {
    if (!signedIn || !canAccessLegalReviewApi) return;
    setLoading(true);
    try {
      const { data } = await legalReviewsApi.list();
      setReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [signedIn, canAccessLegalReviewApi]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fid = currentIssuer?.legalReviewFirmId;
    if (!fid || !signedIn) {
      setFirmLabel('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await legalReviewFirmsApi.getById(fid);
        const name = data?.organizationName ?? data?.OrganizationName ?? '';
        if (!cancelled) setFirmLabel(name || String(fid));
      } catch {
        if (!cancelled) setFirmLabel(String(fid));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentIssuer?.legalReviewFirmId, signedIn]);

  const legalStatusCounts = useMemo(() => {
    const c = { all: reviews.length, pending: 0, inreview: 0, approved: 0, rejected: 0, other: 0 };
    for (const r of reviews) {
      c[getReviewStatusTabKey(r)] += 1;
    }
    return c;
  }, [reviews]);

  const legalStatusTabs = useMemo(() => {
    const keys = new Set();
    for (const r of reviews) {
      keys.add(getReviewStatusTabKey(r));
    }
    const order = ['pending', 'inreview', 'approved', 'rejected', 'other'];
    const sorted = order.filter((k) => keys.has(k));
    return [
      { id: 'all', label: 'All reviews' },
      ...sorted.map((id) => ({ id, label: LEGAL_STATUS_TAB_LABELS[id] || formatEnumLabel(id) }))
    ];
  }, [reviews]);

  useEffect(() => {
    if (legalStatusTab === 'all') return;
    const ids = new Set(legalStatusTabs.map((t) => t.id));
    if (!ids.has(legalStatusTab)) setLegalStatusTab('all');
  }, [legalStatusTabs, legalStatusTab]);

  const rowsAfterLegal = useMemo(() => {
    if (legalStatusTab === 'all') return reviews;
    return reviews.filter((r) => getReviewStatusTabKey(r) === legalStatusTab);
  }, [reviews, legalStatusTab]);

  const assetStatusCounts = useMemo(() => {
    const c = { all: rowsAfterLegal.length };
    for (const r of rowsAfterLegal) {
      const a = nestedAsset(r);
      const k = assetStatusCanonical(assetStatusRaw(a));
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [rowsAfterLegal]);

  const assetStatusTabs = useMemo(() => {
    const keys = new Set();
    for (const r of rowsAfterLegal) {
      const a = nestedAsset(r);
      keys.add(assetStatusCanonical(assetStatusRaw(a)));
    }
    const sorted = [...keys].sort((a, b) => assetStatusSortIndex(a) - assetStatusSortIndex(b));
    return [{ id: 'all', label: 'All assets' }, ...sorted.map((id) => ({ id, label: formatEnumLabel(id) }))];
  }, [rowsAfterLegal]);

  useEffect(() => {
    if (assetStatusTab === 'all') return;
    const ids = new Set(assetStatusTabs.map((t) => t.id));
    if (!ids.has(assetStatusTab)) setAssetStatusTab('all');
  }, [assetStatusTabs, assetStatusTab]);

  const rowsAfterAsset = useMemo(() => {
    if (assetStatusTab === 'all') return rowsAfterLegal;
    return rowsAfterLegal.filter((r) => {
      const a = nestedAsset(r);
      return assetStatusCanonical(assetStatusRaw(a)) === assetStatusTab;
    });
  }, [rowsAfterLegal, assetStatusTab]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rowsAfterAsset;
    return rowsAfterAsset.filter((r) => {
      const a = nestedAsset(r);
      const blob = [
        assetTitle(a),
        assetNumber(a),
        assetCountry(a),
        formatSectorLabel(assetSectorRaw(a)),
        formatEnumLabel(assetStatusRaw(a)),
        getReviewStatusMeta(r).label,
        reviewIdStr(r),
        String(reviewAssetId(r) || ''),
        tokenizerOrg(a)
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rowsAfterAsset, query]);

  const closeDetailModal = () => {
    setDetailModal({ open: false, loading: false, error: null, mapped: null, raw: null });
  };

  const openAssetDetail = async (assetId) => {
    if (!assetId) return;
    setDetailModal({ open: true, loading: true, error: null, mapped: null, raw: null });
    try {
      const { data } = await assetsApi.getById(assetId);
      setDetailModal({
        open: true,
        loading: false,
        error: null,
        mapped: mapAssetToFrontend(data),
        raw: data
      });
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to load asset';
      setDetailModal((m) => ({ ...m, loading: false, error: msg }));
      toast.error(msg);
    }
  };

  const openDecisionModal = (mode, reviewId) => {
    if (!reviewId) return;
    setDecisionModal({ open: true, mode, reviewId, notes: '' });
  };

  const closeDecisionModal = () => {
    setDecisionModal({ open: false, mode: null, reviewId: null, notes: '' });
  };

  const submitDecision = async () => {
    const { mode, reviewId, notes } = decisionModal;
    if (!reviewId || !mode) return;

    const trimmed = notes.trim();
    if (mode === 'reject' && !trimmed) {
      toast.error('Add rejection notes');
      return;
    }
    if (mode === 'requestDocs' && !trimmed) {
      toast.error('Describe which documents or information you need');
      return;
    }

    setSubmittingReviewId(reviewId);
    try {
      if (mode === 'approve') {
        await legalReviewsApi.approve(reviewId, { decisionNotes: trimmed || 'Approved' });
        toast.success('Legal review approved');
      } else if (mode === 'reject') {
        await legalReviewsApi.reject(reviewId, { decisionNotes: trimmed });
        toast.success('Legal review rejected');
      } else {
        await legalReviewsApi.addComment(reviewId, {
          comment: `[Additional documents requested] ${trimmed}`
        });
        toast.success('Request recorded on the review');
      }
      closeDecisionModal();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Action failed');
    } finally {
      setSubmittingReviewId(null);
    }
  };

  const renderDetailModal = () => {
    const { open, loading: dLoading, error, mapped, raw } = detailModal;
    const meta = mapped?.metadata || {};
    const docs = mapped?.documents || [];
    const ac = raw?.assetClass ?? raw?.AssetClass;
    const tok = raw?.authorizedTokenizer ?? raw?.AuthorizedTokenizer;

    return (
      <AnimatePresence>
        {open && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDetailModal}
          >
            <ModalCard
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Asset details</h2>
                <IconOnlyButton type="button" onClick={closeDetailModal} aria-label="Close">
                  <FiX />
                </IconOnlyButton>
              </ModalHeader>
              <ModalBody>
                {dLoading && <p style={{ color: '#a0a0a0' }}>Loading…</p>}
                {error && !dLoading && <p style={{ color: '#ff8a8a' }}>{error}</p>}
                {!dLoading && !error && mapped && (
                  <>
                    <DetailGrid>
                      <dt>Title</dt>
                      <dd>{mapped.name || mapped.title || '—'}</dd>
                      <dt>Asset #</dt>
                      <dd>{mapped.assetNumber || '—'}</dd>
                      <dt>Sector</dt>
                      <dd>{formatSectorLabel(mapped.sector)}</dd>
                      <dt>Status</dt>
                      <dd>{formatEnumLabel(mapped.status)}</dd>
                      <dt>Country</dt>
                      <dd>{mapped.country || '—'}</dd>
                      <dt>Valuation (USD)</dt>
                      <dd>
                        {mapped.valuationUSD != null
                          ? Number(mapped.valuationUSD).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : '—'}
                      </dd>
                      <dt>Legal owner</dt>
                      <dd>{mapped.custodian || '—'}</dd>
                      <dt>Owner identifier</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{mapped.ownerAddress || '—'}</dd>
                      <dt>Asset class</dt>
                      <dd>{pick(ac, 'name', 'Name') || '—'}</dd>
                      <dt>Tokenizer</dt>
                      <dd>{pick(tok, 'organizationName', 'OrganizationName') || '—'}</dd>
                      <dt>Asset ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{mapped.id}</dd>
                    </DetailGrid>
                    <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem', color: '#9bd6ff' }}>Metadata draft</h3>
                    <JsonBlock>{JSON.stringify(meta, null, 2)}</JsonBlock>
                    <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem', color: '#9bd6ff' }}>
                      Documents ({docs.length})
                    </h3>
                    {docs.length === 0 ? (
                      <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>No documents on file.</p>
                    ) : (
                      <DocTable>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>File</th>
                            <th>IPFS CID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => (
                            <tr key={d.id || d.Id || d.fileName}>
                              <td>{d.documentType || d.DocumentType || '—'}</td>
                              <td>{d.fileName || d.FileName || '—'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                {d.ipfsCid || d.IpfsCid || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </DocTable>
                    )}
                  </>
                )}
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    );
  };

  const renderDecisionModal = () => {
    const { open, mode, notes, reviewId } = decisionModal;
    if (!open || !mode) return null;

    const titles = {
      approve: 'Approve legal review',
      reject: 'Reject legal review',
      requestDocs: 'Request additional documents'
    };

    const hints = {
      approve: 'Optional notes are stored with the approval decision.',
      reject: 'Rejection notes are required and are shared with the review record.',
      requestDocs:
        'This posts a comment on the review so the tokenizer team can see what to provide. Be specific about missing documents or clarifications.'
    };

    const placeholders = {
      approve: 'Optional decision notes…',
      reject: 'Reason for rejection (required)…',
      requestDocs: 'e.g. Please upload updated chain-of-custody and insurance certificate…'
    };

    const busy = Boolean(submittingReviewId);

    return (
      <AnimatePresence>
        {open && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDecisionModal}
          >
            <ModalCard
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>{titles[mode]}</h2>
                <IconOnlyButton type="button" onClick={closeDecisionModal} aria-label="Close" disabled={busy}>
                  <FiX />
                </IconOnlyButton>
              </ModalHeader>
              <ModalBody>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem', lineHeight: 1.5 }}>{hints[mode]}</p>
                <p style={{ margin: '0.5rem 0 0', color: '#64748b', fontSize: '0.75rem' }}>
                  Review ID: <span style={{ fontFamily: 'monospace' }}>{reviewId}</span>
                </p>
                <ModalTextArea
                  value={notes}
                  onChange={(e) => setDecisionModal((m) => ({ ...m, notes: e.target.value }))}
                  placeholder={placeholders[mode]}
                  disabled={busy}
                  aria-label={mode === 'reject' || mode === 'requestDocs' ? 'Required notes' : 'Optional notes'}
                />
              </ModalBody>
              <ModalFooter>
                <Button type="button" $variant="secondary" onClick={closeDecisionModal} disabled={busy}>
                  Cancel
                </Button>
                <Button type="button" onClick={submitDecision} disabled={busy}>
                  {busy ? 'Submitting…' : mode === 'approve' ? 'Confirm approval' : mode === 'reject' ? 'Confirm rejection' : 'Send request'}
                </Button>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    );
  };

  if (!signedIn) {
    return (
      <Page>
        <Hero>
          <h1>
            <FiBriefcase />
            Firm legal assets
          </h1>
          <p>Sign in to view assets in your firm&apos;s legal review scope.</p>
        </Hero>
        <Notice>Use the Login control in the header.</Notice>
      </Page>
    );
  }

  if (!canAccessLegalReviewApi) {
    return (
      <Page>
        <Hero>
          <h1>
            <FiBriefcase />
            Firm legal assets
          </h1>
          <p>You need the LegalReviewer, Admin, or Auditor role.</p>
        </Hero>
        <Notice>Ask an administrator if you need access.</Notice>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        <h1>
          <FiBriefcase />
          Firm legal assets
        </h1>
        <p>
          Assets with legal reviews that match your firm&apos;s registered sectors
          {firmLabel ? ` (${firmLabel})` : ''}.{' '}
          {isAdmin || isAuditor
            ? 'Administrators and auditors see every review on the platform.'
            : 'Use filters to narrow by legal review status and underlying asset status, then search.'}{' '}
          For cases <strong>in review</strong> that are <strong>assigned to you</strong>, you can approve, reject, or
          request additional documents from this table. Open the{' '}
          <Link to="/legal-reviews" style={{ color: '#7dd3fc' }}>legal review queue</Link> for assignment and the full
          detail view.
        </p>
      </Hero>

      {isLegalReviewer && !isAdmin && (
        <Notice>
          Only assets in sectors your firm is approved for (or reviews assigned to you) appear here. Approve, reject, or
          request documents using the row actions when the legal status is <strong>In review</strong> and you are the
          assigned reviewer.
        </Notice>
      )}

      <Toolbar>
        <Button type="button" onClick={load} disabled={loading}>
          <FiRefreshCw />
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
        <Link to="/legal-reviews" style={{ textDecoration: 'none' }}>
          <Button type="button" $variant="secondary">
            <FiShield />
            Legal review queue
          </Button>
        </Link>
      </Toolbar>

      {reviews.length > 0 && (
        <>
          <FilterBlock>
            <FilterLabel>Legal review status</FilterLabel>
            <FilterTabBar role="tablist" aria-label="Legal review status">
              {legalStatusTabs.map((tab) => (
                <FilterTab
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={legalStatusTab === tab.id}
                  $active={legalStatusTab === tab.id}
                  onClick={() => {
                    setLegalStatusTab(tab.id);
                    setAssetStatusTab('all');
                  }}
                >
                  {tab.label}
                  <FilterCount $active={legalStatusTab === tab.id}>{legalStatusCounts[tab.id] ?? 0}</FilterCount>
                </FilterTab>
              ))}
            </FilterTabBar>
          </FilterBlock>

          <FilterBlock>
            <FilterLabel>Asset workflow status</FilterLabel>
            <FilterTabBar role="tablist" aria-label="Asset status">
              {assetStatusTabs.map((tab) => (
                <FilterTab
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={assetStatusTab === tab.id}
                  $active={assetStatusTab === tab.id}
                  onClick={() => setAssetStatusTab(tab.id)}
                >
                  {tab.label}
                  <FilterCount $active={assetStatusTab === tab.id}>{assetStatusCounts[tab.id] ?? 0}</FilterCount>
                </FilterTab>
              ))}
            </FilterTabBar>
          </FilterBlock>
        </>
      )}

      <SearchWrap>
        <FiSearch aria-hidden />
        <input
          type="search"
          placeholder="Search asset title, #, sector, tokenizer, legal status, ids…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search assets"
        />
      </SearchWrap>

      {(query.trim() || legalStatusTab !== 'all' || assetStatusTab !== 'all') && reviews.length > 0 && (
        <p style={{ margin: '-0.25rem 0 1rem', fontSize: '0.8rem', color: '#94a3b8' }}>
          {query.trim()
            ? `Showing ${filtered.length} of ${rowsAfterAsset.length} in current filters`
            : `${rowsAfterAsset.length} case${rowsAfterAsset.length === 1 ? '' : 's'} after filters (${reviews.length} total)`}
        </p>
      )}

      {!loading && reviews.length === 0 && (
        <Empty>
          <strong>No reviews in scope</strong>
          Nothing to show yet, or your firm has no matching sector coverage.
        </Empty>
      )}

      {reviews.length > 0 && rowsAfterLegal.length === 0 && legalStatusTab !== 'all' && (
        <Empty>
          <strong>No reviews with this legal status</strong>
          Choose another legal review tab.
        </Empty>
      )}

      {reviews.length > 0 &&
        rowsAfterLegal.length > 0 &&
        rowsAfterAsset.length === 0 &&
        assetStatusTab !== 'all' && (
          <Empty>
            <strong>No assets with this workflow status</strong>
            Choose another asset status filter.
          </Empty>
        )}

      {reviews.length > 0 &&
        rowsAfterAsset.length > 0 &&
        filtered.length === 0 &&
        query.trim() && (
          <Empty>
            <strong>No matches</strong>
            Adjust search — {rowsAfterAsset.length} case{rowsAfterAsset.length === 1 ? '' : 's'} in current filters.
          </Empty>
        )}

      {filtered.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Asset</th>
                <th>Asset #</th>
                <th>Sector</th>
                <th>Tokenizer</th>
                <th>Asset status</th>
                <th>Legal review</th>
                <th>Review id</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const a = nestedAsset(r);
                const aid = reviewAssetId(r);
                const legalMeta = getReviewStatusMeta(r);
                return (
                  <tr key={reviewIdStr(r)}>
                    <td style={{ fontWeight: 600, color: '#f8fafc' }}>{a ? assetTitle(a) : '—'}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <FiHash aria-hidden style={{ opacity: 0.5 }} />
                        {a ? assetNumber(a) || '—' : '—'}
                      </span>
                    </td>
                    <td>{a ? formatSectorLabel(assetSectorRaw(a)) : '—'}</td>
                    <td>{a ? tokenizerOrg(a) || '—' : '—'}</td>
                    <td>
                      <Pill>{a ? formatEnumLabel(assetStatusRaw(a)) : '—'}</Pill>
                    </td>
                    <td>
                      <Pill title={legalMeta.label}>{legalMeta.label}</Pill>
                    </td>
                    <td>
                      <Mono title={reviewIdStr(r)}>{reviewIdStr(r).slice(0, 8)}…</Mono>
                    </td>
                    <ActionsCell>
                      <RowActionsWrap>
                        {canDecideAssignedReview(r) && (
                          <>
                            <RowActionBtn
                              type="button"
                              $tone="ok"
                              disabled={!!submittingReviewId}
                              title="Approve legal review"
                              onClick={() => openDecisionModal('approve', reviewIdStr(r))}
                            >
                              <FiCheck aria-hidden />
                              Approve
                            </RowActionBtn>
                            <RowActionBtn
                              type="button"
                              $tone="bad"
                              disabled={!!submittingReviewId}
                              title="Reject legal review"
                              onClick={() => openDecisionModal('reject', reviewIdStr(r))}
                            >
                              <FiSlash aria-hidden />
                              Reject
                            </RowActionBtn>
                            <RowActionBtn
                              type="button"
                              $tone="neutral"
                              disabled={!!submittingReviewId}
                              title="Request additional documents (adds a review comment)"
                              onClick={() => openDecisionModal('requestDocs', reviewIdStr(r))}
                            >
                              <FiFileText aria-hidden />
                              Request docs
                            </RowActionBtn>
                          </>
                        )}
                        <IconOnlyButton
                          type="button"
                          title="Full asset information"
                          aria-label="Asset details"
                          disabled={!aid || !!submittingReviewId}
                          onClick={() => openAssetDetail(aid)}
                        >
                          <FiInfo />
                        </IconOnlyButton>
                      </RowActionsWrap>
                    </ActionsCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {renderDetailModal()}
      {renderDecisionModal()}
    </Page>
  );
};

export default LegalFirmAssets;
